import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {demoPeer, sendDemoInviteToChat, useDemoFamily} from '#/lib/demoFamily'
import {type NavigationProp} from '#/lib/routes/types'
import {shareText} from '#/lib/sharing'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {SendViaChatDialog} from '#/components/dms/dialogs/ShareViaChatDialog'
import {ArrowOutOfBox_Stroke2_Corner0_Rounded as ArrowOutOfBoxIcon} from '#/components/icons/ArrowOutOfBox'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {FamilyRecipientConfirmDialog} from './FamilyRecipientConfirmDialog'

/**
 * Chooser under the QR card: "Send in a Taderok chat" opens the DM picker and
 * drops the recipient into that conversation with the invite link pre-filled in
 * the composer; "Other apps" uses the native share sheet (WhatsApp/SMS get the
 * invite card via the OG link preview, web copies the link).
 */
export function FamilyShareDialog({
  control,
  link,
  code,
  message,
}: {
  control: Dialog.DialogControlProps
  link: string
  /** The family code — included when sharing through other apps. */
  code?: string | null
  /** Pre-filled composer text; includes the sender's profile name when known. */
  message?: string
}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const sendViaChatControl = Dialog.useDialogControl()
  const recipientConfirmControl = Dialog.useDialogControl()
  const demo = useDemoFamily()
  const [pendingChatId, setPendingChatId] = useState<string | null>(null)

  // Other apps: share a clear text that includes BOTH the deep link (join
  // Taderok) and the family code — WhatsApp/SMS/Telegram show the invite
  // card via the OG preview; on web the text goes to the clipboard.
  const onShareViaOtherApps = useCallback(() => {
    const codeLine = code ? `\nFamily code: ${code}` : ''
    control.close(() => {
      void shareText(`${message ?? link}${codeLine}`)
    })
  }, [control, link, code, message])

  // Demo mode: sending goes through the same recipient-confirm step (with the
  // simulated peer) so the flow behaves exactly like the real one.
  const onSendToDemoPeer = useCallback(() => {
    const peer = demoPeer(demo.role)
    sendDemoInviteToChat()
    Toast.show(
      _(msg`Invitation sent to ${peer.displayName} — open the chat to see it`),
      {type: 'success'},
    )
    // DemoConversation is a common screen in every stack, so this works on
    // web (flat) and native (tabs) alike.
    navigation.navigate('DemoConversation')
  }, [demo.role, navigation, _])

  // Real mode: once a chat is picked, confirm WHO will receive it before
  // opening the composer — no more sending to the wrong person by mistake.
  const onSelectChat = useCallback(
    (chatId: string) => {
      setPendingChatId(chatId)
      recipientConfirmControl.open()
    },
    [recipientConfirmControl],
  )

  const onSendToChat = useCallback(() => {
    if (!pendingChatId) return
    control.close(() => {
      navigation.navigate('MessagesConversation', {
        conversation: pendingChatId,
        draftText: message ?? link,
      })
    })
  }, [control, link, message, navigation, pendingChatId])

  return (
    <>
      <Dialog.Outer control={control}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={_(msg`Share family invitation`)}>
          <View style={[a.gap_lg]}>
            <View style={[a.gap_xs]}>
              <Text style={[a.text_2xl, a.font_bold]}>
                <Trans>Share invitation</Trans>
              </Text>
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                <Trans>How do you want to send the invite link?</Trans>
              </Text>
            </View>
            <View style={[a.gap_sm]}>
              {demo.enabled ? (
                <Button
                  label={_(msg`Send to ${demoPeer(demo.role).displayName} (demo)`)}
                  size="large"
                  color="primary"
                  onPress={() => recipientConfirmControl.open()}>
                  <ButtonText>
                    <Trans>
                      Send to {demoPeer(demo.role).displayName} (demo)
                    </Trans>
                  </ButtonText>
                </Button>
              ) : (
                <Button
                  label={_(msg`Send via direct message`)}
                  size="large"
                  color="primary"
                  onPress={() => sendViaChatControl.open()}>
                  <ButtonText>
                    <Trans>Send via direct message</Trans>
                  </ButtonText>
                </Button>
              )}
              <Button
                label={_(msg`Share via other apps`)}
                size="large"
                color="secondary"
                onPress={onShareViaOtherApps}>
                <ButtonIcon icon={ArrowOutOfBoxIcon} />
                <ButtonText>
                  <Trans>Other apps</Trans>
                </ButtonText>
              </Button>
            </View>
            {demo.enabled ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>
                  Demo mode: sends the invitation to a simulated chat with{' '}
                  {demoPeer(demo.role).displayName}.
                </Trans>
              </Text>
            ) : null}
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>

      {!demo.enabled && (
        <SendViaChatDialog
          control={sendViaChatControl}
          onSelectChat={onSelectChat}
        />
      )}

      <FamilyRecipientConfirmDialog
        control={recipientConfirmControl}
        chatId={demo.enabled ? undefined : (pendingChatId ?? undefined)}
        demoPeer={demo.enabled ? demoPeer(demo.role) : undefined}
        onSend={demo.enabled ? onSendToDemoPeer : onSendToChat}
      />
    </>
  )
}
